import { supabase } from '../../lib/supabase';

// Cache to prevent multiple simultaneous calls for the same user
const pendingChildFetches = new Map();

/**
 * Fetch child profile from the database
 * @param {string} userId - The authenticated user's ID (auth_user_id)
 * @returns {Promise<{data: any, error: any}>} - Result of the profile fetch
 */
export const fetchChildProfile = async (userId) => {
  // Check if there's already a pending fetch for this user
  if (pendingChildFetches.has(userId)) {
    // eslint-disable-next-line no-console
    console.log('🔄 Child profile fetch already in progress, waiting...');
    return pendingChildFetches.get(userId);
  }

  // Create a promise for this fetch and cache it
  const fetchPromise = (async () => {
    try {
      const { data, error } = await supabase
        .from('children')
        .select('*')
        .eq('auth_user_id', userId)
        .single();

      if (error) {
        console.error('❌ Error fetching child profile:', error);
        return { data: null, error };
      }

      // eslint-disable-next-line no-console
      console.log('✅ Child profile fetched successfully:', data);
      return { data, error: null };
    } catch (error) {
      console.error('💥 Exception caught in fetchChildProfile:', error);
      return { data: null, error };
    } finally {
      // Always clean up the cache when done
      pendingChildFetches.delete(userId);
    }
  })();

  // Cache the promise
  pendingChildFetches.set(userId, fetchPromise);

  return fetchPromise;
};

/**
 * Update child profile information
 * @param {string} childId - The child's UUID
 * @param {object} updateData - Data to update
 * @returns {Promise<{data: any, error: any}>} - Result of the update operation
 */
export const updateChildProfile = async (childId, updateData) => {
  try {
    const { data, error } = await supabase
      .from('children')
      .update(updateData)
      .eq('id', childId)
      .select()
      .single();

    if (error) {
      console.error('❌ Error updating child profile:', error);
      return { data: null, error };
    }

    // eslint-disable-next-line no-console
    console.log('✅ Child profile updated successfully:', data);
    return { data, error: null };
  } catch (error) {
    console.error('💥 Exception caught in updateChildProfile:', error);
    return { data: null, error };
  }
};

/**
 * Get child progress summary
 * @param {string} childId - The child's UUID
 * @returns {Promise<{data: any, error: any}>} - Result of the progress fetch
 */
export const getChildProgressSummary = async (childId) => {
  try {
    const { data, error } = await supabase
      .from('progress')
      .select(
        `
        *,
        level:levels(
          *,
          game:games(*),
          word:words(id, word, length)
        )
      `
      )
      .eq('child_id', childId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('❌ Error fetching child progress:', error);
      return { data: [], error };
    }

    // Calculate summary statistics
    const summary = {
      total_levels: data.length,
      completed: data.filter((p) => p.level_status === 'completed').length,
      in_progress: data.filter((p) => p.level_status === 'inprogress').length,
      failed: data.filter((p) => p.level_status === 'failed').length,
      completion_rate: 0,
      recent_progress: data.slice(0, 5), // Last 5 activities
    };

    if (summary.total_levels > 0) {
      summary.completion_rate =
        (summary.completed / summary.total_levels) * 100;
    }

    // eslint-disable-next-line no-console
    console.log('✅ Child progress summary fetched successfully:', summary);
    return { data: summary, error: null };
  } catch (error) {
    console.error('💥 Exception caught in getChildProgressSummary:', error);
    return { data: null, error };
  }
};
